import BaseService from '../../../services/BaseService';
import { normalizeText } from '../selectSearchFilter';
import {
  filterMunicipioOptions,
  MIN_MUNICIPIO_QUERY_LENGTH,
} from './municipioSelectSearch';

const STORAGE_KEY = 'municipioSelectV2_options';
const STORAGE_TS_KEY = 'municipioSelectV2_options_ts';
const CACHE_TTL_MS = 12 * 60 * 60 * 1000;

let memoryCache = null;
let memoryCacheLoadedAt = null;
let inflightWarmPromise = null;

const mapMunicipioOption = (item) => {
  const nome = item.nomeOriginal ?? item.nome ?? '';
  const uf = item.uf ?? null;
  const label = uf ? `${nome} / ${uf}` : nome;
  const codigoIbge = item.codigoIbge != null ? String(item.codigoIbge) : '';

  if (!codigoIbge) {
    return null;
  }

  return {
    value: codigoIbge,
    label,
    searchKey: normalizeText(nome),
    codigoIbge,
    nome,
    uf,
    municipio: {
      codigoIbge,
      nomeOriginal: nome,
      uf,
    },
  };
};

const normalizeResponseItems = (response) => {
  const body = response?.data;

  if (Array.isArray(body)) {
    return body;
  }

  if (Array.isArray(body?.data)) {
    return body.data;
  }

  if (Array.isArray(body?.content)) {
    return body.content;
  }

  return [];
};

const mapResponseToOptions = (response) => (
  normalizeResponseItems(response)
    .map(mapMunicipioOption)
    .filter(Boolean)
);

const isCacheFresh = (loadedAt) => (
  loadedAt != null && Date.now() - loadedAt < CACHE_TTL_MS
);

const readStorageCache = () => {
  try {
    const loadedAt = Number(localStorage.getItem(STORAGE_TS_KEY));
    if (!isCacheFresh(loadedAt)) {
      return null;
    }

    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) {
      return null;
    }

    return { options: parsed, loadedAt };
  } catch {
    return null;
  }
};

const writeStorageCache = (options) => {
  if (!options?.length) {
    return;
  }

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(options));
    localStorage.setItem(STORAGE_TS_KEY, String(Date.now()));
  } catch {
    // localStorage indisponível ou quota excedida — segue só em memória
  }
};

const hydrateMemoryCache = (options, loadedAt = Date.now()) => {
  memoryCache = options;
  memoryCacheLoadedAt = loadedAt;
  return memoryCache;
};

const mergeIntoCache = (incoming) => {
  if (!incoming?.length) {
    return getCachedMunicipioOptions();
  }

  const current = getCachedMunicipioOptions();
  const merged = new Map(current.map((option) => [option.value, option]));
  incoming.forEach((option) => merged.set(option.value, option));

  const options = Array.from(merged.values());
  hydrateMemoryCache(options);
  writeStorageCache(options);
  return options;
};

/**
 * Retorna o cache atual (memória ou localStorage válido por 12h).
 * Não dispara request HTTP.
 */
export function getCachedMunicipioOptions() {
  if (memoryCache && isCacheFresh(memoryCacheLoadedAt)) {
    return memoryCache;
  }

  const stored = readStorageCache();
  if (stored) {
    return hydrateMemoryCache(stored.options, stored.loadedAt);
  }

  return [];
}

const fetchMunicipiosFromApi = async ({ nome, uf, codigoIbge } = {}) => {
  const params = new URLSearchParams();
  if (nome) params.set('nome', nome);
  if (uf) params.set('uf', uf);
  if (codigoIbge) params.set('codigoIbge', codigoIbge);

  const query = params.toString();
  const url = query ? `municipios?${query}` : 'municipios';
  const response = await BaseService.get({ url });
  return mapResponseToOptions(response);
};

const fetchOptionsMunicipiosByCodigoIbge = async (codigos) => {
  try {
    const response = await BaseService.get({ url: 'options/municipios' });
    const wanted = new Set(codigos.map(String));
    return normalizeResponseItems(response)
      .filter((item) => wanted.has(String(item.codigoIbge)))
      .map((item) => mapMunicipioOption({
        ...item,
        nomeOriginal: item.nomeOriginal ?? item.nome,
      }))
      .filter(Boolean);
  } catch {
    return [];
  }
};

/**
 * Resolve labels de municípios por código IBGE e mescla no cache.
 * Tenta GET municipios?codigoIbge=...; fallback em options/municipios.
 */
export async function resolveMunicipioOptionsByCodigoIbge(codigos) {
  const normalized = [...new Set((codigos || []).map(String).filter(Boolean))];
  if (!normalized.length) {
    return getCachedMunicipioOptions();
  }

  const cached = getCachedMunicipioOptions();
  const cachedByValue = new Map(cached.map((option) => [option.value, option]));
  const missing = normalized.filter((code) => !cachedByValue.has(code));

  if (!missing.length) {
    return cached;
  }

  let resolved = [];

  try {
    resolved = await fetchMunicipiosFromApi({ codigoIbge: missing.join(',') });
  } catch {
    resolved = [];
  }

  const resolvedValues = new Set(resolved.map((option) => option.value));
  const stillMissing = missing.filter((code) => !resolvedValues.has(code));

  if (stillMissing.length) {
    const fallback = await fetchOptionsMunicipiosByCodigoIbge(stillMissing);
    resolved = [...resolved, ...fallback];
  }

  if (resolved.length) {
    return mergeIntoCache(resolved);
  }

  return getCachedMunicipioOptions();
}

/**
 * Tenta carregar a lista completa em background (GET municipios sem params).
 * Se o backend não suportar, falha silenciosamente.
 */
export async function warmMunicipioCache() {
  const cached = getCachedMunicipioOptions();
  if (cached.length > 0) {
    return cached;
  }

  if (inflightWarmPromise) {
    return inflightWarmPromise;
  }

  inflightWarmPromise = fetchMunicipiosFromApi()
    .then((options) => {
      if (options.length > 0) {
        return mergeIntoCache(options);
      }
      return getCachedMunicipioOptions();
    })
    .catch(() => getCachedMunicipioOptions())
    .finally(() => {
      inflightWarmPromise = null;
    });

  return inflightWarmPromise;
}

/**
 * Busca municípios para o select:
 * 1. filtra cache local
 * 2. se vazio, consulta GET municipios?nome=...
 * 3. mescla no cache (12h) e refiltra
 */
export async function searchMunicipioOptions(query, { limit } = {}) {
  const trimmed = (query ?? '').trim();
  const normalizedQuery = normalizeText(trimmed);

  if (!normalizedQuery || normalizedQuery.length < MIN_MUNICIPIO_QUERY_LENGTH) {
    return [];
  }

  const cached = getCachedMunicipioOptions();
  const localResults = filterMunicipioOptions(cached, trimmed, { limit });

  if (localResults.length > 0) {
    return localResults;
  }

  try {
    const incoming = await fetchMunicipiosFromApi({ nome: trimmed });
    if (!incoming.length) {
      return [];
    }

    const merged = mergeIntoCache(incoming);
    const mergedResults = filterMunicipioOptions(merged, trimmed, { limit });
    return mergedResults.length > 0 ? mergedResults : incoming.slice(0, limit);
  } catch {
    return [];
  }
}

/** @deprecated use getCachedMunicipioOptions + warmMunicipioCache */
export function getMunicipioOptions() {
  const cached = getCachedMunicipioOptions();
  if (cached.length > 0) {
    return Promise.resolve(cached);
  }
  return warmMunicipioCache();
}

export async function fetchAndMergeMunicipios({ nome, uf } = {}) {
  const incoming = await fetchMunicipiosFromApi({ nome, uf });
  return mergeIntoCache(incoming);
}
